import {
	Component,
	Output,
	EventEmitter,
	Input,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatterDispatch } from '@state/matter';
import { MatterFields } from '@models/matter';
import { Store } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { TestHooks } from './closing-date.hooks';
import { AI_GENERATED_ICON } from '@core/util/icon';
import { tap, map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

@Component({
	selector: 'app-sale-closing-date',
	templateUrl: './closing-date.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ClosingDateComponent {
	testHooks = TestHooks;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	private minDate: string;
	private closingDate: string;

	@Input() label = 'Closing Date';
	@Input() orientation = 'row';
	@Input() skeletonLoading = false;
	@Output() onFocus = new EventEmitter<string>();
	public closingDate$ = this.store.select(SelectedMatter.closingDate).pipe(
		tap((date) => {
			this.closingDate = date;
		}),
	);
	public closingDateFieldMapping$ = this.store
		.select(SelectedMatter.getFieldMappings)
		.pipe(map((fieldMappings) => fieldMappings?.closingDate));
	public field = MatterFields.CLOSING_DATE;

	private minDateData$ = combineLatest(
		[
			SelectedMatter.agreementDate,
			SelectedMatter.titleSearchCompletionDate,
		].map((selector) => this.store.select(selector)),
	).pipe(
		map(([agreementDate, titleSearchCompletionDate]) => {
			if (agreementDate && titleSearchCompletionDate) {
				if (agreementDate > titleSearchCompletionDate) {
					return {
						minDate: titleSearchCompletionDate,
						minDateLabel: 'Title Search Completion Date',
					};
				} else {
					return {
						minDate: agreementDate,
						minDateLabel: 'Agreement Date',
					};
				}
			} else if (agreementDate) {
				return {
					minDate: agreementDate,
					minDateLabel: 'Agreement Date',
				};
			} else if (titleSearchCompletionDate) {
				return {
					minDate: titleSearchCompletionDate,
					minDateLabel: 'Title Search Completion Date',
				};
			}
			return {};
		}),
	);

	public minDateLabel$ = this.minDateData$.pipe(
		map(({ minDateLabel }) => minDateLabel),
	);
	public minDate$ = this.minDateData$.pipe(
		map(({ minDate }) => minDate),
		tap((date) => {
			this.minDate = date;
		}),
	);

	constructor(
		private matterDispatch: MatterDispatch,
		private store: Store,
	) {}

	_onFocus() {
		this.onFocus.emit(this.field);
	}

	onChange = (event: string) => {
		if (
			event !== this.closingDate &&
			(!event || !this.minDate || event >= this.minDate)
		) {
			this.matterDispatch.OverwriteFields('closingDate', event);
		}
	};
}
